import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { IFaq, IObject } from '../../../../type_definitions/datatypes';
import { FaqDataService } from '../../../shared/faq/faq-data.service';
import { StateService, UiOnParamsChanged } from '@uirouter/angular';

export interface IFaqResponse {
  selectedFaqs?:ISelectedFaq[];
  generatedAnswer?:string;
}

export interface ISelectedFaq extends IFaq {
  hitRate:number
}

export interface IFaqRequest {
  question?:string;
  context?:string;
}

@Component({
    selector: 'faq-search',
    template:
      `
        <h2>FAQ Search <info-link [(linkVariable)]="showComments"></info-link></h2> 
        <p *ngIf="showComments">Effortlessly find answers to your questions using AI-powered FAQ search. You can change the <a (click)="toggleContext($event)">context here</a> to have more tailored made answers.</p>
        <br>
        <div class="search-form" *ngIf="!loading">
          <form novalidate>
            <formly [form]="form" [fields]="findFields" [model]="findModel">
              <button class="btn btn-primary" [busyButton]="searching" (click)="search()" translate>GA.BUTTONS.SEARCH</button>
            </formly>
          </form>
        </div>

        <h2 *ngIf="searching">
          <activity-indicator text="GA.GENERAL.SEARCHING"></activity-indicator>
        </h2>

        <h3 *ngIf="noResultsFound" translate>GA.GENERAL.NO_RESULTS_FOUND</h3>

        <div *ngIf="!loading && faqResponse" class="faq-list">
          <h2 class="keyword-title">Selected FAQs</h2>
          <div *ngIf="faqResponse.selectedFaqs?.length">
            <div *ngFor="let selectedFaq of faqResponse.selectedFaqs; trackById">
              <faq-link [hideInfoIcon]="true" [faqCode]="selectedFaq.id" [data]="selectedFaq"></faq-link>
            </div>
          </div><br>
          <div class="alert alert-warning">
            <i class="fa fa fa-exclamation-triangle"></i> 
            Please note that following text is AI generated and can have mistakes
            <ul>
              <li>always double check the generated text</li>
              <li>do not copy paste it to any external parties</li>
              <li>only use responses that match the information in the FAQs</li>
            </ul>
          </div>
          <h2 class="keyword-title">Generated answer</h2>
          <div>
            <div [innerHTML]="faqResponse.generatedAnswer"></div>
          </div>
        </div>
      `,
    styleUrls: [ './faq.scss' ],
    encapsulation: ViewEncapsulation.None,
    preserveWhitespaces: true
  }
)
export class FaqSearchComponent implements OnInit, UiOnParamsChanged {

  @Input() public question:string;
  public loading:boolean;
  public searching:boolean;
  public noResultsFound:boolean;
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public findFields:IExtraFieldConfigurationObject[];
  public faqResponse:IFaqResponse;
  public showComments:boolean = false;
  public findModel:IFaqRequest = {
    context : 'You are an assistant who checks and chooses relevant answers given in a previous conversation. Your task is to combine these answers and generate a user-friendly response. Follow these steps:\n' +
      '\n' +
      '1. Prioritize by Hit Rate: Start with the answers that have the highest hit rate.\n' +
      '2. Check for Completeness: Evaluate if a high-ranking answer fully addresses the question. If it does, you can stop and use that answer.\n' +
      '3. Combine Relevant Information: If the top answer is incomplete, combine it with relevant information from other answers to provide a comprehensive response.\n' +
      '4. Construct User-Friendly Text: Ensure the final response is clear, coherent, and actionable for the user.\n' +
      '5. Cite Sources: List references at the end of your response, using the URLs from the answers you used. URL should be clickable. Avoid duplicates.\n' +
      '6. Do not use fillers at the end of the generated text, strict to the information found in the answers.'
  };

  private hideContext:boolean = true;

  constructor(
    private formlyService:GaFormlyService,
    private faqDataService:FaqDataService,
    private stateService: StateService
  ) {
  }

  public ngOnInit():void {
    this.findModel.question = this.question;
    this.createFields();
    if (this.findModel.question) {
      this.search();
    }
  }

  public search():void {
    if (this.findModel.question && this.findModel.context) {
      // update query parameter to have up-to-date question model
      this.stateService.go(this.stateService.current.name, { question: this.findModel.question }, { location: true });
      this.searchFaqs();
    }
  }

  private createFields():void {
    this.findFields = [
      this.formlyService.anInput('question', 'Question')
        .isRequired()
        .withPlaceholder('GA.GENERAL.SEARCH_TEXT')
        .withExtraOptions({ templateOptions: { inputFocus: true } })
        .withInputType('search')
        .build(),
      this.formlyService.aTextarea('context', 'Context')
        .isRequired()
        .withPlaceholder('GA.GENERAL.SEARCH_TEXT')
        .withExtraOptions({ templateOptions: { inputFocus: true } })
        .withHideExpression(() => this.hideContext)
        .build()
    ];
  }

  public uiOnParamsChanged(newParams:IObject):void {
    if (newParams?.question) {
      this.form.patchValue({ question: newParams.question });
    }
  }

  public toggleContext(event:Event):void {
    event.preventDefault();
    this.hideContext = !this.hideContext;
  }

  private searchFaqs():void {
    this.noResultsFound = false;

    this.setBusy(true);
    this.faqDataService.searchFaq(this.findModel)
      .then((faqResponse:IFaqResponse) => {
        this.noResultsFound = !faqResponse;
        this.faqResponse = faqResponse;
      })
      .finally(() => {
        this.setBusy(false);
      });
  }

  private setBusy(busy:boolean):void {
    if (this.findModel.question) {
      this.searching = busy;
    } else {
      this.loading = busy;
    }
  }

}

